import { useContext, useEffect, useId, useRef, type ReactNode, type KeyboardEvent } from 'react'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { Button, Pressable, StatusChip } from '../../Common'
import { Chart } from '../../Common/Chart/Chart.js'
import type { ChartResult } from '../../../types/chart.js'
import { createAnalysisChartSpec } from '../popover/analysisChartMapping.js'
import { useNls } from '../../../nls/nlsProvider'
import type { ChartDescriptor } from '../charts'
import { usePoppableCard } from '../popover/usePoppableCard'
import { useChartPopover } from '../popover/useChartPopover'
import type { AnalysisRowResolution } from '../../../types/analysis'
import { FreshnessChip } from './FreshnessChip'
import { TimelineContext } from '../../../context/timelineContextCore'

export type LiveModuleRecord = {
  id: string
  primary: string
  secondary?: string
  numeric?: number
  drillTarget?: string | null
}

export type LiveModuleHeadline = {
  value: number | string
  delta?: number
  label?: string
}

export type LiveModuleSparkline = {
  points: number[]
  direction?: 'up-is-good' | 'up-is-bad'
}

export type LiveModuleCardProps = {
  icon: ReactNode
  title: string
  eyebrow?: string
  headline: LiveModuleHeadline
  sparkline?: LiveModuleSparkline
  records: LiveModuleRecord[]
  descriptor?: ChartDescriptor | null
  loading?: boolean
  highlighted?: boolean
  gated?: boolean
  gatedMessage?: string
  emptyMessage?: string
  emptyState?: { heading: string; detail: string }
  onRecordClick?: (record: LiveModuleRecord) => void
  className?: string
  resolutionActions?: (record: LiveModuleRecord) => { currentResolution?: AnalysisRowResolution; canEdit: boolean; onAcknowledge: () => void; onDismiss: () => void; onConvertToTask: () => void; onReopen: () => void }
  lastSyncedAt?: Date | null
  subscriptionError?: Error | null
  onRefresh?: () => void
}

const InlineSparkline = ({
  points,
  direction = 'up-is-good',
  label,
  delta,
  timeRange,
}: {
  points: number[]
  direction?: 'up-is-good' | 'up-is-bad'
  label: string
  delta: number
  timeRange: string
}) => {
  const { t } = useNls()
  const descriptionId = useId()
  const width = 160
  const height = 44
  const safePoints = points.length > 0 ? points : [0]
  const isFlat = safePoints.every((p) => p === 0)

  const spec = createAnalysisChartSpec({
    id: `sparkline-${label}`,
    type: 'line',
    style: {
      legend: 'off',
      seriesColor: {
        sparkline: isFlat
          ? 'var(--border)'
          : direction === 'up-is-bad'
            ? 'var(--chart-4)'
            : 'var(--chart-2)',
      },
    },
  })
  const data: ChartResult = {
    series: [
      {
        key: 'sparkline',
        label: 'Trend',
        points: safePoints.map((val, idx) => ({ x: `t${idx}`, y: val })),
      },
    ],
    asOf: new Date().toISOString(),
  }

  const description = t('NLSAnalysisSparklineAriaDescription', { label, direction: points.at(-1)! > points[0]! ? t('NLSAnalysisSparklineDirectionUp') : points.at(-1)! < points[0]! ? t('NLSAnalysisSparklineDirectionDown') : t('NLSAnalysisSparklineDirectionFlat'), delta: Math.abs(delta).toFixed(1), timeRange })
  return (
    <>
      <div
        className={`analysisLiveModuleCard__sparklineSvg${isFlat ? ' isFlat' : ''}`}
        role="img"
        aria-describedby={descriptionId}
      >
        <Chart spec={spec} data={data} width={width} height={height} />
      </div>
      <span id={descriptionId} className="a11yVisuallyHidden">{description}</span>
    </>
  )
}

export const LiveModuleCard = ({
  icon,
  title,
  eyebrow,
  headline,
  sparkline,
  records,
  descriptor,
  loading = false,
  highlighted = false,
  gated = false,
  gatedMessage,
  emptyMessage,
  emptyState,
  onRecordClick,
  className = '',
  resolutionActions,
  lastSyncedAt,
  subscriptionError,
  onRefresh,
}: LiveModuleCardProps) => {
  const { t } = useNls()
  const timeline = useContext(TimelineContext)
  const resolvedLastSyncedAt = lastSyncedAt ?? timeline?.elementsLastSyncedAt
  const resolvedSubscriptionError = subscriptionError ?? timeline?.elementsSubscriptionError
  const resolvedRefresh = onRefresh ?? timeline?.refreshElementsSubscription
  const { cardProps } = usePoppableCard(descriptor)
  const { registerModule } = useChartPopover()
  const registryElementRef = useRef<HTMLDivElement | null>(null)
  const resolvedEyebrow = eyebrow ?? t('NLSAnalysisLiveModuleEyebrowGovernance')
  const resolvedGatedMessage = gatedMessage ?? t('NLSAnalysisLiveModuleGated')
  const resolvedEmptyMessage = emptyMessage ?? t('NLSAnalysisLiveModuleEmpty')
  const displayRecords = records.slice(0, 5)

  const handleKeyDown = (e: KeyboardEvent, record: LiveModuleRecord) => {
    if ((e.key === 'Enter' || e.key === ' ') && onRecordClick) {
      e.preventDefault()
      e.stopPropagation()
      onRecordClick(record)
    }
  }

  const formatHeadlineNumber = (val: number | string) => {
    if (typeof val === 'number') {
      return new Intl.NumberFormat().format(val)
    }
    return val
  }

  const delta = headline.delta ?? 0
  const sparklineTimeRange = t('NLSAnalysisTimeRange7D')
  const isPositiveDelta = delta > 0
  const isNegativeDelta = delta < 0
  const isUpIsBad = sparkline?.direction === 'up-is-bad'

  // Delta tone: if up-is-bad, positive delta is red/warn, negative delta is good.
  const deltaTone = isPositiveDelta
    ? isUpIsBad ? 'isWarn' : 'isGood'
    : isNegativeDelta
      ? isUpIsBad ? 'isGood' : 'isWarn'
      : 'isNeutral'

  useEffect(() => {
    if (!descriptor || !registryElementRef.current) return undefined
    return registerModule(descriptor.id, descriptor, registryElementRef.current)
  }, [descriptor, registerModule])

  return (
    <div ref={registryElementRef}>
      <RailPageShellCard
      {...cardProps}
      className={`analysisLiveModuleCard ${highlighted ? 'analysisLiveModuleCard--highlighted' : ''} ${className}${cardProps.className ? ` ${cardProps.className}` : ''}`}
      data-testid={`live-module-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <header className="analysisLiveModuleCard__header">
        <div className="analysisLiveModuleCard__meta">
          <span className="analysisLiveModuleCard__icon" aria-hidden="true">
            {icon}
          </span>
          <div>
            <span className="settingsEyebrow">{resolvedEyebrow}</span>
            <h3 className="analysisLiveModuleCard__title">
              {title}
              {highlighted && (
                <span title={t('NLSAnalysisLiveModuleAnomalyTooltip')}>
                  <StatusChip
                    className="analysisLiveModuleCard__anomalyBadge"
                    tone="warning"
                    size="sm"
                    icon={<PriorityHighRoundedIcon fontSize="inherit" />}
                  >
                    <span className="srOnly">{t('NLSAnalysisLiveModuleAnomalyTooltip')}</span>
                  </StatusChip>
                </span>
              )}
            </h3>
          </div>
          <FreshnessChip lastSyncedAt={resolvedLastSyncedAt} />
        </div>
      </header>

      {resolvedSubscriptionError ? (
        <div className="analysisLiveModuleCard__gated"><p>{t('NLSAnalysisSubscriptionErrorMessage')}</p><Button variant="tertiary" size="sm" onClick={resolvedRefresh}>{t('NLSAnalysisSubscriptionErrorAction')}</Button></div>
      ) : gated ? (
        <div className="analysisLiveModuleCard__gated">
          <LockRoundedIcon fontSize="small" />
          <p>{resolvedGatedMessage}</p>
        </div>
      ) : loading ? (
        <div className="analysisLiveModuleCard__loading" aria-busy="true" aria-label={t('NLSAnalysisLiveModuleLoading')}>
          <div className="analysisLiveModuleCard__skeletonHeadline" />
          <div className="analysisLiveModuleCard__skeletonSparkline" />
          <div className="analysisLiveModuleCard__skeletonList" />
        </div>
      ) : (
        <>
          <div className="analysisLiveModuleCard__hero">
            <div className="analysisLiveModuleCard__headlineBlock">
              <strong className="analysisLiveModuleCard__value">
                {formatHeadlineNumber(headline.value)}
              </strong>
              {headline.label && (
                <span className="analysisLiveModuleCard__unit">{headline.label}</span>
              )}
              {delta !== 0 && (
                <span className={`analysisLiveModuleCard__delta ${deltaTone}`}>
                  {isPositiveDelta ? `+${delta}` : delta}
                </span>
              )}
            </div>

            {sparkline && (
              <div className="analysisLiveModuleCard__sparklineShell">
                <InlineSparkline
                  points={sparkline.points}
                  direction={sparkline.direction}
                  label={title}
                  delta={delta}
                  timeRange={sparklineTimeRange}
                />
              </div>
            )}
          </div>

          <div className="analysisLiveModuleCard__recordsSection">
            <span className="analysisLiveModuleCard__recordsHeading">{t('NLSAnalysisLiveModuleTopFive')}</span>
            {displayRecords.length > 0 ? (
              <ul className="analysisLiveModuleCard__recordsList" role="list">
                {displayRecords.map((record) => {
                  const isClickable = Boolean(onRecordClick)
                  const resolution = resolutionActions?.(record)
                  const resolutionLabel = resolution?.currentResolution?.state === 'ACK' ? t('NLSAnalysisRowResolutionChipAck') : resolution?.currentResolution?.state === 'DISMISSED' ? t('NLSAnalysisRowResolutionChipDismissed') : t('NLSAnalysisRowResolutionChipConverted')
                  return (
                    <li
                      key={record.id}
                      className={`analysisLiveModuleCard__recordRow ${isClickable ? 'isClickable' : ''}`}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRecordClick?.(record)
                      }}
                      onKeyDown={(e) => handleKeyDown(e, record)}
                      aria-label={`${record.primary}${record.secondary ? ` · ${record.secondary}` : ''}`}
                    >
                      <div className="analysisLiveModuleCard__recordBody">
                        <strong className="analysisLiveModuleCard__recordPrimary">{record.primary}</strong>
                        {record.secondary && (
                          <small className="analysisLiveModuleCard__recordSecondary">{record.secondary}</small>
                        )}
                      </div>
                      {typeof record.numeric === 'number' && (
                        <span className="analysisLiveModuleCard__recordNumeric">
                          {record.numeric}
                        </span>
                      )}
                      {resolution?.currentResolution ? (
                        <StatusChip
                          tone={
                            resolution.currentResolution.state === 'CONVERTED'
                              ? 'success'
                              : resolution.currentResolution.state === 'ACK'
                              ? 'info'
                              : 'muted'
                          }
                          size="sm"
                        >
                          {resolutionLabel}
                        </StatusChip>
                      ) : null}
                      {resolution && resolution.canEdit ? <span className="analysisRowResolution__actions">{resolution.currentResolution ? <Pressable className="analysisRowResolution__pill" onClick={(event) => { event.stopPropagation(); resolution.onReopen() }}>{t('NLSAnalysisRowResolutionReopenAction')}</Pressable> : <><Pressable className="analysisRowResolution__pill analysisRowResolution__pill--ack" onClick={(event) => { event.stopPropagation(); resolution.onAcknowledge() }}>{t('NLSAnalysisRowResolutionAcknowledgeAction')}</Pressable><Pressable className="analysisRowResolution__pill analysisRowResolution__pill--dismissed" onClick={(event) => { event.stopPropagation(); resolution.onDismiss() }}>{t('NLSAnalysisRowResolutionDismissAction')}</Pressable><Pressable className="analysisRowResolution__pill analysisRowResolution__pill--convert" onClick={(event) => { event.stopPropagation(); resolution.onConvertToTask() }}>{t('NLSAnalysisRowResolutionConvertAction')}</Pressable></>}</span> : null}
                      {isClickable && (
                        <ChevronRightRoundedIcon fontSize="small" className="analysisLiveModuleCard__recordChevron" />
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : emptyState ? (
              <div className="analysisLiveModuleCard__emptyState">
                <strong>{emptyState.heading}</strong>
                <small>{emptyState.detail}</small>
              </div>
            ) : (
              <p className="analysisLiveModuleCard__empty">{resolvedEmptyMessage}</p>
            )}
          </div>
        </>
      )}
      </RailPageShellCard>
    </div>
  )
}
